import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Tag,
  Button,
  Spin,
  Row,
  Col,
  Layout,
  Typography,
  Divider,
  Badge,
  Progress,
  Empty,
  Statistic,
  Modal,
  Alert,
  Select,
} from "antd";
import {
  ArrowLeftOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  CompassOutlined,
  RocketOutlined,
  TeamOutlined,
  SafetyCertificateOutlined,
  GlobalOutlined,
  StarFilled,
  UserOutlined,
} from "@ant-design/icons";
import { useAlternativeCareerPaths } from "Hooks/AdminHooks/Organization/useAlternativeCareerPaths";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import "./AlternativePaths.scss";
import { ArrowRightOutlined } from "@mui/icons-material";

const { Content } = Layout;
const { Text, Title } = Typography;

const AlternativePathsPage = () => {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const {
    careerPaths,
    loadingCareerPaths,
    fetchAlternativeCareerPaths,
    loadingPerformanceReview,
    performanceReview,
    fetchPerformanceReview,
    loadingSupervisors,
    supervisors,

    selectedPath,
    selectedSupervisor,
    setSelectedSupervisor,
    selectedJob,
    setSelectedJob,
    modalVisible,
    setModalVisible,

    handleOpenModal,
    handleSubmitTransition,
    handleJobChange
  } = useAlternativeCareerPaths(employeeId);

  useEffect(() => {
    if (employeeId) {
      fetchAlternativeCareerPaths(employeeId);
      fetchPerformanceReview(employeeId);
    }
  }, [employeeId]);

  if (loadingCareerPaths || loadingPerformanceReview) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <div className="loading-container">
          <Spin size="large" tip="Aggregating Career Insights..." />
        </div>
      </DashboardLayout>
    );
  }

  const paths = careerPaths?.alternativePaths || [];
  const currentPath = careerPaths?.currentPath;
  const employee = careerPaths?.employee;
  const review = performanceReview?.performanceReview;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Content className="career-analytics-container">
        {/* --- HEADER --- */}
        <div className="header-luxury">
          <div className="header-main-info">
            <Title level={2} className="m-0">
              Career Trajectory Analytics
            </Title>
            <Text type="secondary" className="m-0">
              Growth strategy for{" "}
              <span className="highlight-text">{employee?.fullName || review?.revieweeName}</span>
            </Text>
          </div>

          <Button
            icon={<ArrowLeftOutlined />}
            type="link"
            onClick={() => navigate(-1)}
            className="btn-back"
          >
            Back
          </Button>
        </div>

        {/* --- PERFORMANCE SCORECARD --- */}
        {review && (
          <div className="performance-scorecard-row">
            <div className="section-label">
              <StarFilled /> Performance Standing
            </div>
            <Card className="performance-main-card" bordered={false}>
              <Row gutter={[24, 24]} align="middle">
                <Col xs={24} md={6} className="rating-summary">
                  <Statistic
                    title="Overall Rating"
                    value={review.performanceRatingCode?.name}
                    prefix={<Badge status="processing" color="#52c41a" />}
                  />
                  <div className="score-badge">
                    <Text strong>{review.totalPerformanceScore}%</Text>
                    <small>Score</small>
                  </div>
                </Col>

                <Col xs={24} md={18}>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={8}>
                      <div className="mini-metric">
                        <Text type="secondary">Accountability</Text>
                        <Progress
                          percent={review.weightedAccountabilityScore}
                          strokeColor="#1890ff"
                          size="small"
                          format={(percent) => `${percent}/50`}
                        />
                      </div>
                    </Col>
                    <Col xs={24} sm={8}>
                      <div className="mini-metric">
                        <Text type="secondary">Performance Factors</Text>
                        <Progress
                          percent={(review.weightedPerformanceFactorsScore / 40) * 100}
                          success={{ percent: (review.weightedPerformanceFactorsScore / 40) * 100 }}
                          size="small"
                          format={() => `${review.weightedPerformanceFactorsScore}/40`}
                        />
                      </div>
                    </Col>
                    <Col xs={24} sm={8}>
                      <div className="mini-metric">
                        <Text type="secondary">Org. Behaviors</Text>
                        <Progress
                          percent={(review.weightedDynamicOrganizationScore / 10) * 100}
                          strokeColor="#52c41a"
                          size="small"
                          format={() => `${review.weightedDynamicOrganizationScore}/10`}
                        />
                      </div>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Card>
          </div>
        )}

        {/* --- DOMAINS & SKILLS ROW --- */}
        <div className="analytics-summary-row mt-4">
          <Row gutter={[8, 8]}>
            <Col xs={24} lg={12}>
              <Card className="summary-pill-card" bordered={false}>
                <div className="summary-icon domain-icon">
                  <GlobalOutlined />
                </div>
                <div className="summary-content">
                  <Text className="summary-label">Domain Knowledge of Employee</Text>
                  <div className="summary-tags">
                    {currentPath?.domainKnowledges?.map((domain) => (
                      <Tag key={domain.domainKnowledgeId} className="luxury-tag domain">
                        {domain.name} <small>({domain.industry})</small>
                      </Tag>
                    ))}
                  </div>
                </div>
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card className="summary-pill-card" bordered={false}>
                <div className="summary-icon skill-icon">
                  <SafetyCertificateOutlined />
                </div>
                <div className="summary-content">
                  <Text className="summary-label">Skills of Employee</Text>
                  <div className="summary-tags">
                    {employee?.employeeSkills?.map((skill) => (
                      <Tag key={skill.skillId} className="luxury-tag skill">
                        {skill.skillName}
                      </Tag>
                    ))}
                  </div>
                </div>
              </Card>
            </Col>
          </Row>
        </div>

        {/* --- CURRENT PATH SECTION --- */}
        {currentPath && currentPath.jobTitles && (
          <div className="path-container current-path mt-4">
            <div className="section-label mb-1">
              <CompassOutlined /> Current Trajectory: <strong>{currentPath.name}</strong>
              <Tag color="gold" className="ms-2" style={{ fontSize: "10px" }}>
                {currentPath.publicId}
              </Tag>
            </div>

            {/* Career Path Level Description */}
            {currentPath.description && (
              <div className="mb-2 px-2">
                <Text type="secondary" style={{ fontSize: "13px" }}>
                  {currentPath.description}
                </Text>
              </div>
            )}

            {/* Domains for Current Path */}
            {currentPath.domainKnowledges?.length > 0 && (
              <div className="mb-4 px-2">
                <div
                  className="text-uppercase mb-2"
                  style={{
                    fontSize: "10px",
                    fontWeight: 700,
                    color: "#8c8c8c",
                    letterSpacing: "0.5px",
                  }}
                >
                  Domains
                </div>
                <div className="d-flex flex-wrap gap-1">
                  {currentPath.domainKnowledges.map((dk) => (
                    <Tag
                      key={dk.domainKnowledgeId}
                      color="blue"
                      style={{ borderRadius: "4px", fontSize: "11px" }}
                    >
                      {dk.name} <small style={{ opacity: 0.8 }}>({dk.industry})</small>
                    </Tag>
                  ))}
                </div>
              </div>
            )}

            <div className="horizontal-roadmap">
              {currentPath.jobTitles
                ?.sort((a, b) => a.stepOrder - b.stepOrder)
                .map((job) => {
                  const isCurrent = job.isCurrent;
                  return (
                    <div key={job.id} className="roadmap-segment">
                      <div className={`roadmap-node ${isCurrent ? "is-active" : ""}`}>
                        {isCurrent && (
                          <div className="status-tag pulse-active">Current Position</div>
                        )}

                        <Card className="node-card" bordered={false}>
                          <div className="d-flex justify-content-between align-items-start mb-1">
                            <Title level={5} className="job-name m-0">
                              {job.name}
                            </Title>
                            <Tag className="m-0" style={{ fontSize: "9px" }}>
                              {job.publicId}
                            </Tag>
                          </div>

                          {/* Job Description */}
                          <Text
                            type="secondary"
                            className="d-block mb-2"
                            style={{ fontSize: "11px", lineHeight: "1.3" }}
                          >
                            {job.description}
                          </Text>

                          <Text className="exp-label">
                            {job.maxExperienceRequired
                              ? `${job.minExperienceRequired}-${job.maxExperienceRequired} Years`
                              : `${job.minExperienceRequired}+ Years`}
                          </Text>

                          <Divider className="node-divider" />

                          <div className="skill-grid">
                            {job.requiredSkills?.map((skill) => (
                              <Tag
                                key={skill.skillId}
                                className={`skill-pill ${skill.hasSkill ? "earned" : "missing"}`}
                                icon={
                                  skill.hasSkill ? <CheckCircleFilled /> : <CloseCircleFilled />
                                }
                              >
                                {skill.skillName}
                              </Tag>
                            ))}
                          </div>

                          <div className="node-meta mt-3">
                            <span>
                              <TeamOutlined style={{ marginRight: "4px" }} />
                              {job.employeeCount} in role
                            </span>
                            {job.skillMatchPercentage > 0 && (
                              <span className="ms-auto text-primary" style={{ fontWeight: 600 }}>
                                {Math.round(job.skillMatchPercentage)}% Match
                              </span>
                            )}
                          </div>
                        </Card>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {/* --- ALTERNATIVE PATHS --- */}
        <div className="section-label mt-5">
          <RocketOutlined /> Transition Opportunities
        </div>

        {paths.length === 0 ? (
          <Empty description="No Alternative Trajectories Identified" />
        ) : (
          <Row gutter={[24, 24]}>
            {paths.map((path) => {
              const hasEligibleRole = path.jobTitles?.some((j) => j.meetsExperienceRequirement);

              return (
                <Col xs={24} lg={12} xxl={8} key={path.id}>
                  <Card
                    className={`modern-path-card h-100 ${
                      path.canTransition ? "can-transition" : ""
                    }`}
                  >
                    {/* Career Path Header */}
                   <div className="card-header-glass">
  <div className="d-flex flex-column">
    {/* Use align-items-center to keep the title and button on the same horizontal line */}
    <div className="d-flex align-items-center gap-2">
      <Title level={5} className="m-0">
        {path.name}
      </Title>

      {path.canTransition && (
        <Button
          size="small"
          icon={<ArrowRightOutlined />}
          onClick={() => handleOpenModal(path, employeeId)}
          className="btn-primary"
          style={{
            fontSize: "11px",
            height: "24px",
            padding: "0 10px",
            borderRadius: "4px",
            display: "flex",
            alignItems: "center",
          }}
        >
          Start Transition
        </Button>
      )}
    </div>

    <Text type="secondary" className="small">
      ID: {path.publicId}
    </Text>
  </div>

  <div className="match-score">
    <span className="score-text">{Math.round(path.matchPercentage)}%</span>
    <span className="score-label">Fit</span>
  </div>
</div>

                    <div className="card-body-content px-3 mt-3">
                      {/* Career Path Description */}
                      {path.description && (
                        <div className="mb-3">
                          <Text
                            type="secondary"
                            style={{ fontSize: "12px", display: "block", lineHeight: "1.4" }}
                          >
                            {path.description}
                          </Text>
                        </div>
                      )}

                      {/* Domains Section */}
                      {path.domainKnowledges?.length > 0 && (
                        <div className="mb-3">
                          <div
                            className="text-uppercase mb-1"
                            style={{
                              fontSize: "10px",
                              fontWeight: 700,
                              color: "#8c8c8c",
                              letterSpacing: "0.5px",
                            }}
                          >
                            Domains
                          </div>
                          <div className="d-flex flex-wrap gap-1">
                            {path.domainKnowledges.map((dk) => (
                              <Tag
                                key={dk.domainKnowledgeId}
                                color="blue"
                                className="m-0"
                                style={{ fontSize: "10px", borderRadius: "4px" }}
                              >
                                {dk.name}
                              </Tag>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="path-vertical-steps mt-2">
                      {path.jobTitles?.map((job) => (
                        <div
                          key={job.id}
                          className={`vertical-step ${job.isSuggested ? "Suggested" : ""}`}
                        >
                          <div className="step-indicator">
                            {job.meetsExperienceRequirement ? (
                              <CheckCircleFilled className="text-success" />
                            ) : (
                              <div className="lock-dot" />
                            )}
                          </div>

                          <div className="step-content">
                            {/* Job Title & Employee Count */}
                            <div className="d-flex justify-content-between align-items-start">
                              <div style={{ flex: 1 }}>
                                <Text strong className="d-block">
                                  {job.name}
                                </Text>
                                <div className="d-flex align-items-center gap-2 mt-1">
                                  <Tag className="m-0" style={{ fontSize: "10px" }}>
                                    {job.publicId}
                                  </Tag>
                                  {/* Employee Count */}
                                  <span style={{ fontSize: "11px", color: "#8c8c8c" }}>
                                    <UserOutlined style={{ marginRight: "4px" }} />
                                    {job.employeeCount || 0} Peers
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Job Description */}
                            <Text type="secondary" className="d-block my-2 small italic">
                              {job.description}
                            </Text>

                            {/* Experience Requirements */}
                            <div className="experience-requirement mb-2">
                              <Badge
                                status={job.meetsExperienceRequirement ? "success" : "default"}
                                text={
                                  <Text className="small">
                                    Exp:{" "}
                                    {job.maxExperienceRequired
                                      ? `${job.minExperienceRequired} - ${job.maxExperienceRequired} years`
                                      : `${job.minExperienceRequired}+ Years`}
                                  </Text>
                                }
                              />
                            </div>

                            {/* Required Skills */}
                            <div className="d-flex flex-wrap gap-1 mt-2">
                              {job.requiredSkills?.length > 0 ? (
                                job.requiredSkills.map((skill) => (
                                  <Tag
                                    key={skill.skillId}
                                    className={`modern-tag ${skill.hasSkill ? "has" : "needs"}`}
                                  >
                                    {skill.skillName}
                                  </Tag>
                                ))
                              ) : (
                                <Text type="secondary" className="small">
                                  No specific skills required
                                </Text>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
    <Modal
  title={`Transition to ${selectedPath?.name}`}
  open={modalVisible}
  onCancel={() => setModalVisible(false)}
  onOk={() => handleSubmitTransition()}
  okText="Confirm Transition"
  okButtonProps={{
    disabled:
      !selectedJob ||
      !selectedSupervisor ||
      loadingSupervisors ||
      supervisors.length === 0,
    className: "btn-primary",
  }}
>
  {/* ✅ STEP 1: JOB TITLE */}
  <div className="mb-3">
    <Text strong>Select Job Title</Text>

    <Select
      placeholder="Select Job Title"
      style={{ width: "100%" }}
      value={selectedJob}
      onChange={(value) => handleJobChange(value)}
      options={selectedPath?.jobTitles?.map((job) => ({
        value: job.id,
        label: (
          <div className="d-flex justify-content-between">
            <span>{job.name}</span>
            {job.isSuggested && (
              <Tag color="green" style={{ marginLeft: 8 }}>
                Recommended
              </Tag>
            )}
          </div>
        ),
      }))}
    />
  </div>

  {/* ✅ STEP 2: SUPERVISOR FLOW */}
  {!selectedJob ? (
    <Alert
      type="info"
      message="Please select a job title first"
      showIcon
    />
  ) : loadingSupervisors ? (
    <div className="text-center py-3">
      <Spin />
    </div>
  ) : supervisors.length === 0 ? (
    <Alert
      type="warning"
      message="No supervisors available"
      description="No supervisors found for the selected job title."
      showIcon
    />
  ) : (
    <div className="mb-3">
      <Text strong>Select Supervisor</Text>

   <Select
  placeholder="Select Supervisor"
  style={{ width: "100%" }}
  value={selectedSupervisor}
  onChange={(value) => setSelectedSupervisor(value)}
  optionLabelProp="label" // 👈 important for selected display
>
  {supervisors.map((sup) => (
    <Select.Option
      key={sup.id}
      value={sup.id}
      label={sup.fullName} 
    >
      <div className="d-flex flex-column">
        {/* 👤 Name */}
        <span style={{ fontWeight: 500 }}>{sup.fullName}</span>

        {/* 💼 Job + Designation */}
        <span style={{ fontSize: 12, color: "#888" }}>
          {sup.jobTitleName} • {sup.designationName}
        </span>
      </div>
    </Select.Option>
  ))}
</Select>
    </div>
  )}
</Modal>
      </Content>
    </DashboardLayout>
  );
};

export default AlternativePathsPage;
